import { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Send, Bot, User, Loader2 } from "lucide-react";
import { API_CONFIG } from "@/infrastructure/config/api";

interface WidgetMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

/**
 * Standalone widget chat page for embedding in external sites.
 * Route: /widget/chat/:workspaceId
 * Query params: theme (light|dark), title (string)
 *
 * This page is fully public (no auth required) and renders
 * without the Prodgy navbar/sidebar layout.
 */
export default function WidgetChat() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams] = useSearchParams();

  const theme = searchParams.get("theme") || "light";
  const title = searchParams.get("title") || "Prodgy Chat";

  const [messages, setMessages] = useState<WidgetMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const isDark = theme === "dark";

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || !workspaceId) return;

    const userMessage: WidgetMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }

    try {
      const apiUrl = API_CONFIG.API_URL.endsWith("/")
        ? API_CONFIG.API_URL
        : `${API_CONFIG.API_URL}/`;

      const response = await fetch(
        `${apiUrl}playground/widget/${workspaceId}/message`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            session_id: sessionId,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      const data = await response.json();

      const assistantMessage: WidgetMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: data.data?.content || data.content || "Sorry, I couldn't process that.",
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch {
      const errorMessage: WidgetMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "Sorry, something went wrong. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, workspaceId, sessionId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div
      className={`flex flex-col h-screen w-full ${
        isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
      }`}
      style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
    >
      {/* Header */}
      <div
        className={`flex items-center gap-2 px-4 py-3 border-b shrink-0 ${
          isDark ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
        }`}
      >
        <Bot className={`h-5 w-5 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
        <span className="font-semibold text-sm truncate">{title}</span>
        <span
          className={`ml-auto text-[10px] px-1.5 py-0.5 rounded ${
            isDark ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"
          }`}
        >
          Powered by Prodgy
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center space-y-2">
              <Bot
                className={`h-10 w-10 mx-auto ${
                  isDark ? "text-gray-600" : "text-gray-300"
                }`}
              />
              <p
                className={`text-sm ${
                  isDark ? "text-gray-500" : "text-gray-400"
                }`}
              >
                Ask me anything about {title}
              </p>
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex gap-2 ${
              msg.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {msg.role === "assistant" && (
              <div
                className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                  isDark ? "bg-blue-900/50" : "bg-blue-100"
                }`}
              >
                <Bot
                  className={`h-4 w-4 ${
                    isDark ? "text-blue-400" : "text-blue-600"
                  }`}
                />
              </div>
            )}

            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === "user"
                  ? isDark
                    ? "bg-blue-600 text-white"
                    : "bg-blue-600 text-white"
                  : isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-gray-100 border border-gray-200"
              }`}
            >
              {msg.content}
            </div>

            {msg.role === "user" && (
              <div
                className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                  isDark ? "bg-gray-700" : "bg-gray-200"
                }`}
              >
                <User
                  className={`h-4 w-4 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                />
              </div>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-2 justify-start">
            <div
              className={`shrink-0 h-7 w-7 rounded-full flex items-center justify-center ${
                isDark ? "bg-blue-900/50" : "bg-blue-100"
              }`}
            >
              <Bot
                className={`h-4 w-4 ${
                  isDark ? "text-blue-400" : "text-blue-600"
                }`}
              />
            </div>
            <div
              className={`rounded-lg px-3 py-2 ${
                isDark
                  ? "bg-gray-800 border border-gray-700"
                  : "bg-gray-100 border border-gray-200"
              }`}
            >
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        className={`border-t px-3 py-2 shrink-0 ${
          isDark ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-gray-50"
        }`}
      >
        <div
          className={`flex items-end gap-2 rounded-lg border px-3 py-2 ${
            isDark
              ? "border-gray-600 bg-gray-900 focus-within:border-blue-500"
              : "border-gray-300 bg-white focus-within:border-blue-500"
          }`}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            rows={1}
            disabled={isLoading}
            className={`flex-1 resize-none text-sm outline-none bg-transparent placeholder:text-gray-400 ${
              isDark ? "text-gray-100" : "text-gray-900"
            }`}
            style={{ maxHeight: "120px" }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className={`shrink-0 h-8 w-8 rounded-md flex items-center justify-center transition-colors ${
              !input.trim() || isLoading
                ? isDark
                  ? "bg-gray-700 text-gray-500"
                  : "bg-gray-200 text-gray-400"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
